import React, { Suspense } from 'react'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import ReactDOM from 'react-dom'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout, usePageWidth } from '@fs/zion-ui'
import { NoticeLoading } from '@fs/zion-icon'
import { Router, useRouteMatch } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { useUser } from '@fs/zion-user'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import './locales'

const App = React.lazy(() => import('./App'))

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      integrations: [Sentry.extraErrorDataIntegration({ depth: 4 })],
      // Unhandled `TypeError: Failed to fetch` from third-party beacons (GTM, Google Ads,
      // Dynatrace), not our code. TREE-BUILDER-4PH/4PT. Remove if Zion adds it upstream.
      // `TypeError: Load failed` is Safari's wording of the same error. TREE-BUILDER-1ZM.
      // `Java object is gone` comes from scripts the Facebook/Instagram Android in-app
      // browser injects (iabjs://), not our code. TREE-BUILDER-481.
      // `Object Not Found Matching Id:N, MethodName:..., ParamCount:N` comes from the
      // CefSharp-based link scanner Microsoft Outlook/Defender Safe Links uses, not our code. TREE-BUILDER-4Q3.
      // `swbrowser.inNightMode is not a function` comes from a night-mode script a third-party
      // browser injects into the page (stack is only `<anonymous>`), not our code. TREE-BUILDER-4V3.
      // `Can't find variable: _AutofillCallbackHandler` comes from script the Facebook/Instagram
      // iOS in-app browser evaluates in the page, not our code. TREE-BUILDER-4SM.
      // `The WKWebView was deallocated before the message was delivered` is raised by iOS when an
      // in-app browser's injected script posts to a native message handler after its webview is
      // torn down; this app never uses webkit.messageHandlers. TREE-BUILDER-449.
      // `Adobe Target content delivery is disabled` is reported by @fs/zion-adobe-target when the
      // browser blocks cookies or has mboxDisable set; the app falls back to the default experience. TREE-BUILDER-4PB.
      ignoreErrors: [
        'Failed to fetch',
        'Load failed',
        'Java object is gone',
        'Object Not Found Matching Id',
        'swbrowser.inNightMode is not a function',
        '_AutofillCallbackHandler',
        'The WKWebView was deallocated before the message was delivered',
        'Adobe Target content delivery is disabled',
      ],
      // Legacy Safari app extensions (e.g. PayPal Honey) report frames from their bundle path
      // (/Applications/.../Honey.safariextension/h0.js), which Zion's `extensions/` and
      // `*-extension://` patterns don't match. TREE-BUILDER-489.
      denyUrls: [/\.safariextension\//i],
      debug: process.env.NODE_ENV === 'development',
    })
  )
}

function useHideHeaderFooter() {
  const hasHidehfParam = window?.location?.search.includes('hidehf')
  const hasHidehfSessionStorage = window?.sessionStorage?.getItem('hidehf') === 'true'
  const hidehf = hasHidehfParam || hasHidehfSessionStorage

  if (hidehf) {
    window?.sessionStorage?.setItem('hidehf', 'true')
  }

  const { signedIn, userLoading } = useUser()
  // The WhatsApp landing page is a standalone page, not a wizard, so it keeps the footer for signed-in users.
  // Match the same way as its <Route> so trailing-slash and case variants behave the same.
  const showFooterWhenSignedIn = Boolean(useRouteMatch({ exact: true, path: '/whatsapp' }))

  return {
    hideHeader: userLoading || hidehf,
    hideFooter: (signedIn && !showFooterWhenSignedIn) || userLoading || hidehf,
  }
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

function AppRoot() {
  const { hideHeader, hideFooter } = useHideHeaderFooter()
  const atWidth = usePageWidth()
  const helpChatbot = atWidth({ default: false, xl: true })

  return (
    <Root analytics footer={{ hidden: hideFooter }} header={{ hidden: hideHeader }} helpChatbot={helpChatbot}>
      {/* The Root component will render the header and footer based on the props passed */}
      <AppLayout fullWidth>
        <App />
      </AppLayout>
    </Root>
  )
}

// add any app logic here, languages and translations are configured and available here
function FrontierWrapper() {
  setAppLocales(essentialLocales)

  return (
    <Router basename={base}>
      <AppRoot />
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <React.StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </React.StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister()
